import { useQuery } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';

import {
  reports,
  type ReportPageView,
  type ReportSegmentation,
} from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';

import { TEXTO_PENDENTE } from './celula';
import {
  MESES,
  alternarNaLista,
  anosOferecidos,
  ehChaveDeLista,
  ehChaveUnica,
  trocarUnico,
  type ChaveDeLista,
  type ChaveUnica,
} from './filtro';

/**
 * Cabecalho comum (Mes e Ano) e "Painel de Filtros" da pagina.
 *
 * Mes e Ano sao segmentacoes independentes, como no OnPoint: marcar 2026 e Marco e
 * marco de 2026; so Marco e todo marco. Nada marcado e o periodo inteiro. As outras
 * pesquisas aparecem so na pagina que as tem no OnPoint (o servidor diz quais).
 */
export function PainelDeFiltros({
  pagina,
  busca,
  onChange,
}: {
  pagina: ReportPageView | undefined;
  busca: URLSearchParams;
  onChange: (proxima: URLSearchParams) => void;
}) {
  const filtros = new Set(pagina?.available_filters ?? ['periodo']);
  const anos = busca.getAll('year');
  const meses = busca.getAll('month');
  // O Acumulo nao tem mes: so o "Filtro de Ano" da extracao de bases (BI-181).
  const soAno = filtros.has('ano') && !filtros.has('periodo');

  function alterna(chave: ChaveDeLista, valor: string) {
    onChange(alternarNaLista(busca, chave, valor));
  }

  function troca(chave: ChaveUnica, valor: string | null) {
    onChange(trocarUnico(busca, chave, valor));
  }

  const temFiltro = [...busca.keys()].length > 0;

  return (
    <Card>
      <CardHeader
        title="Painel de filtros"
        description="Mes e ano valem para todas as paginas; as pesquisas, para a pagina aberta."
        action={
          temFiltro ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChange(new URLSearchParams())}
            >
              Limpar filtros
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-col gap-4">
        <Segmentacao
          titulo={soAno ? 'Filtro de ano (extracao de bases)' : 'Ano'}
          opcoes={anosOferecidos().map((ano) => ({
            chave: String(ano),
            rotulo: String(ano),
          }))}
          marcadas={anos}
          onToggle={(valor) => alterna('year', valor)}
        />
        {soAno ? null : (
          <Segmentacao
            titulo="Mes"
            opcoes={MESES.map((rotulo, indice) => ({
              chave: String(indice + 1),
              rotulo,
            }))}
            marcadas={meses}
            onToggle={(valor) => alterna('month', valor)}
          />
        )}

        {(pagina?.segmentations ?? []).map((segmentacao) => (
          <SegmentacaoDaPagina
            key={segmentacao.key}
            segmentacao={segmentacao}
            busca={busca}
            onAlternar={(chave, valor) => alterna(chave, valor)}
            onTrocar={(chave, valor) => troca(chave, valor)}
          />
        ))}

        {filtros.has('status_cotacao') && pagina ? (
          <Segmentacao
            titulo="Status da proposta"
            opcoes={pagina.status_options.map((opcao) => ({
              chave: opcao.key,
              rotulo: opcao.label,
            }))}
            marcadas={busca.getAll('status')}
            onToggle={(valor) => alterna('status', valor)}
          />
        ) : null}
        {filtros.has('status_apolice') && pagina ? (
          <Segmentacao
            titulo="Status da apolice"
            opcoes={pagina.policy_status_options.map((opcao) => ({
              chave: opcao.key,
              rotulo: opcao.label,
            }))}
            marcadas={busca.getAll('policy_status')}
            onToggle={(valor) => alterna('policy_status', valor)}
          />
        ) : null}

        <div className="grid gap-4 md:grid-cols-3">
          {filtros.has('corretora') ? (
            <Pesquisa
              label="Pesquisa de corretora"
              tipo="broker"
              valor={busca.get('broker_id')}
              onChange={(valor) => troca('broker_id', valor)}
            />
          ) : null}
          {filtros.has('tomador') ? (
            <Pesquisa
              label="Pesquisa de tomador"
              tipo="policyholder"
              valor={busca.get('policyholder_id')}
              onChange={(valor) => troca('policyholder_id', valor)}
            />
          ) : null}
          {filtros.has('grupo_economico') ? (
            <Pesquisa
              label="Pesquisa de grupo economico"
              tipo="economic_group"
              valor={busca.get('economic_group')}
              onChange={(valor) => troca('economic_group', valor)}
            />
          ) : null}
          {filtros.has('metrica') && pagina ? (
            <SeletorDeMetrica
              pagina={pagina}
              valor={pagina.filters.metric}
              onChange={(valor) => troca('metric', valor)}
            />
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Seletor de metrica: "Metrica" ("Valor Dinamico Esteira") na Esteira e "Distribuicao
 * da carteira vigente" na Carteira. A opcao sem definicao aparece marcada pendente.
 * Os rotulos ficam escritos aqui porque o dicionario de ajuda os procura no codigo.
 *
 * O valor mostrado e a metrica que o servidor aplicou (filters.metric): o endereco
 * pode trazer a metrica da outra pagina, que o servidor troca pela padrao desta.
 */
function SeletorDeMetrica({
  pagina,
  valor,
  onChange,
}: {
  pagina: ReportPageView;
  valor: string;
  onChange: (valor: string) => void;
}) {
  const comum = {
    value: valor,
    options: pagina.metric_options.map((opcao) => ({
      value: opcao.key,
      label: opcao.pending ? `${opcao.label} (pendente de definicao)` : opcao.label,
    })),
    onChange: (evento: ChangeEvent<HTMLSelectElement>) => onChange(evento.target.value),
  };
  if (pagina.metric_title === 'Metrica') {
    return <Select label="Metrica" {...comum} />;
  }
  return <Select label="Distribuicao da carteira vigente" {...comum} />;
}

/**
 * Segmentacao propria da pagina (P-BI-2): a lista de esteiras, o tipo de cotacao, o
 * numero da proposta, o responsavel, a situacao do endosso, a etapa, o status e a
 * regra do motor. A pendente aparece com o motivo, sem filtrar nada.
 */
function SegmentacaoDaPagina({
  segmentacao,
  busca,
  onAlternar,
  onTrocar,
}: {
  segmentacao: ReportSegmentation;
  busca: URLSearchParams;
  onAlternar: (chave: ChaveDeLista, valor: string) => void;
  onTrocar: (chave: ChaveUnica, valor: string | null) => void;
}) {
  const { param, title } = segmentacao;
  if (segmentacao.pending || !param) {
    return (
      <fieldset className="flex flex-col gap-1.5" disabled>
        <legend className="mb-1.5 text-xs font-semibold text-ink-700">{title}</legend>
        <p className="text-2xs text-ink-500">
          <span className="font-semibold">{TEXTO_PENDENTE}.</span>{' '}
          {segmentacao.pending_reason}
        </p>
      </fieldset>
    );
  }
  if (segmentacao.kind === 'lista' && ehChaveDeLista(param)) {
    if (segmentacao.options.length === 0) {
      return (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-xs font-semibold text-ink-700">{title}</legend>
          <p className="text-2xs text-ink-500">Sem opcoes com movimento.</p>
        </fieldset>
      );
    }
    return (
      <Segmentacao
        titulo={title}
        opcoes={segmentacao.options.map((opcao) => ({
          chave: opcao.key,
          rotulo: opcao.label,
        }))}
        marcadas={busca.getAll(param)}
        onToggle={(valor) => onAlternar(param, valor)}
      />
    );
  }
  if (segmentacao.kind === 'unico' && ehChaveUnica(param)) {
    return (
      <div className="max-w-sm">
        <Select
          label={title}
          value={busca.get(param) ?? ''}
          options={[
            { value: '', label: 'Todos' },
            ...segmentacao.options.map((opcao) => ({
              value: opcao.key,
              label: opcao.label,
            })),
          ]}
          onChange={(evento) => onTrocar(param, evento.target.value || null)}
        />
      </div>
    );
  }
  if (segmentacao.kind === 'texto' && ehChaveUnica(param)) {
    // A chave e o valor do endereco: quando ele muda por fora (Limpar filtros, voltar
    // no navegador), o campo nasce de novo com o valor atual, em vez de guardar o
    // texto velho e reaplica-lo no proximo blur.
    return (
      <FiltroDeTexto
        key={busca.get(param) ?? ''}
        titulo={title}
        valor={busca.get(param)}
        onChange={(valor) => onTrocar(param, valor)}
      />
    );
  }
  return null;
}

/**
 * Filtro digitado (Numero de proposta): vale ao sair do campo ou com Enter. O texto
 * comeca com o valor do endereco; quem chama remonta o campo quando o endereco muda.
 */
function FiltroDeTexto({
  titulo,
  valor,
  onChange,
}: {
  titulo: string;
  valor: string | null;
  onChange: (valor: string | null) => void;
}) {
  const [texto, setTexto] = useState(valor ?? '');
  function aplica() {
    const limpo = texto.trim();
    if (limpo !== (valor ?? '')) onChange(limpo || null);
  }
  return (
    <div className="max-w-sm">
      <Field
        label={titulo}
        value={texto}
        placeholder="Digite e tecle Enter"
        onChange={(evento) => setTexto(evento.target.value)}
        onBlur={aplica}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter') aplica();
        }}
      />
    </div>
  );
}

/** Segmentacao em botoes que ligam e desligam, como a lista do OnPoint. */
function Segmentacao({
  titulo,
  opcoes,
  marcadas,
  onToggle,
}: {
  titulo: string;
  opcoes: { chave: string; rotulo: string }[];
  marcadas: string[];
  onToggle: (valor: string) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1.5 text-xs font-semibold text-ink-700">{titulo}</legend>
      <div className="flex flex-wrap gap-1.5">
        {opcoes.map((opcao) => {
          const marcada = marcadas.includes(opcao.chave);
          return (
            <button
              key={opcao.chave}
              type="button"
              aria-pressed={marcada}
              onClick={() => onToggle(opcao.chave)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors duration-(--duration-fast)',
                marcada
                  ? 'border-brand-500 bg-brand-50 font-semibold text-brand-700'
                  : 'border-line-strong bg-surface text-ink-600 hover:bg-ink-50',
              )}
            >
              {opcao.rotulo}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * Pesquisa suspensa de corretora, tomador ou grupo economico. Procura a partir de duas
 * letras ou digitos, e o servidor ja devolve so o que a sessao alcanca, com o dado
 * pessoal mascarado quando falta a permissao.
 */
function Pesquisa({
  label,
  tipo,
  valor,
  onChange,
}: {
  label: string;
  tipo: 'broker' | 'policyholder' | 'economic_group';
  valor: string | null;
  onChange: (valor: string | null) => void;
}) {
  const [texto, setTexto] = useState('');
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const termo = texto.trim();
  const opcoes = useQuery({
    queryKey: ['bi', 'opcoes', tipo, termo],
    queryFn: ({ signal }) => reports.filterOptions(tipo, termo, signal),
    enabled: termo.length >= 2,
  });

  if (valor) {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-ink-700">{label}</span>
        <div className="flex items-center justify-between gap-2 rounded-md border border-brand-500 bg-brand-50 px-3 py-2 text-sm text-brand-700">
          <span className="truncate">{escolhido ?? 'Selecionado no filtro'}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setEscolhido(null);
              onChange(null);
            }}
          >
            Limpar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Field
        label={label}
        value={texto}
        placeholder="Digite ao menos duas letras"
        onChange={(evento) => setTexto(evento.target.value)}
      />
      {termo.length >= 2 && opcoes.data ? (
        opcoes.data.length === 0 ? (
          <p className="text-2xs text-ink-500">Nada encontrado.</p>
        ) : (
          <ul className="max-h-48 overflow-y-auto rounded-md border border-line bg-surface">
            {opcoes.data.map((opcao) => (
              <li key={opcao.key}>
                <button
                  type="button"
                  onClick={() => {
                    setEscolhido(opcao.label);
                    setTexto('');
                    onChange(opcao.key);
                  }}
                  className="flex w-full flex-col items-start px-3 py-2 text-left text-xs hover:bg-ink-50"
                >
                  <span className="font-medium text-ink-800">{opcao.label}</span>
                  <span className="text-2xs text-ink-500">{opcao.document}</span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}
